import React from 'react';
import { Trans } from 'react-i18next';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { BUBBLECHAT_WEB_PATH } from '@repo/constants';
import { defaultTheme } from '@repo/styles';
import { FormikProps, useFormik } from 'formik';
import { t } from 'i18next';
import * as Yup from 'yup';

import { CheckIcon, CheckIconVariant, CloseIcon, EColorMap } from '../../../Icons';
import { InputFormikWithLimit, RoundBtn } from '../../../primitives';
import SelectFormik from '../../../primitives/TextField/SelectFormik';
import UserRegistrationNumberInputForm from '../../../primitives/TextField/UserRegistrationNumberInputForm';

export interface BankAccountVerifyFormikValues {
  agreeRevenueShareTerms: boolean;
  acceptsPrivacyPolicy: boolean;
  acceptsLegalResponsibility: boolean;
  name: string;
  userRegistrationNumber: string;
  bankCode: string;
  accountNumber: string;
  accountHolder: string;
}

const bankAccountVerifySchema = (username: string) =>
  Yup.object({
    agreeRevenueShareTerms: Yup.boolean().oneOf([true], '창작자 이용약관에 동의해 주세요.'),
    acceptsPrivacyPolicy: Yup.boolean().oneOf([true], '개인 정보 수집 및 이용에 동의해 주세요.'),
    acceptsLegalResponsibility: Yup.boolean().oneOf([true], '법적 책임에 동의해 주세요.'),

    name: Yup.string()
      .matches(/^[가-힣\s]+$/, '실명은 한글로 입력해 주세요.')
      .test('is-dynamic-name', `${t('chat.rewards_real name_id')}`, (value) => {
        return value?.trim() === username.trim();
      })
      .required(`${t('chat.rewards_real name_id')}`),

    accountNumber: Yup.string()
      .test('no-dashes', `${t('chat.rewards_only_number')}`, (value) => !value?.includes('-'))
      .matches(/^\d+$/, `${t('chat.rewards_only_number')}`)
      .required(`${t('chat.rewards_only_number')}`),

    userRegistrationNumber: Yup.string()
      .test('no-dashes', `${t('chat.rewards_only_number')}`, (value) => !value?.includes('-'))
      .matches(/^\d{13}$/, '정확한 주민등록번호를 입력해 주세요.')
      .required(`${t('chat.rewards_only_number')}`),

    bankCode: Yup.string().required('은행 코드를 입력해 주세요.'),

    accountHolder: Yup.string()
      .oneOf([Yup.ref('name')], `${t('chat.rewards_account_holder_name_description')}`)
      .required(`${t('chat.rewards_account_holder_name_description')}`),
  });

const bankAccountModifySchema = (username: string) =>
  Yup.object({
    agreeRevenueShareTerms: Yup.boolean().oneOf([true], '창작자 이용약관에 동의해 주세요.'),
    acceptsPrivacyPolicy: Yup.boolean().oneOf([true], '개인 정보 수집 및 이용에 동의해 주세요.'),
    acceptsLegalResponsibility: Yup.boolean().oneOf([true], '법적 책임에 동의해 주세요.'),

    name: Yup.string()
      .matches(/^[가-힣\s]+$/, '실명은 한글로 입력해 주세요.')
      .test('is-dynamic-name', `${t('chat.rewards_real name_id')}`, (value) => {
        return value === username;
      })
      .required(`${t('chat.rewards_real name_id')}`),

    accountNumber: Yup.string()
      .test('no-dashes', `${t('chat.rewards_only_number')}`, (value) => !value?.includes('-'))
      .matches(/^\d+$/, `${t('chat.rewards_only_number')}`)
      .required(`${t('chat.rewards_only_number')}`),

    userRegistrationNumber: Yup.string(),

    bankCode: Yup.string().required('은행 코드를 입력해 주세요.'),

    accountHolder: Yup.string()
      .oneOf([Yup.ref('name')], `${t('chat.rewards_account_holder_name_description')}`)
      .required(`${t('chat.rewards_account_holder_name_description')}`),
  });

export type BankAccountVerifyBodyProps = {
  form: {
    name: string;
    userRegistrationNumber?: string;
    bankCode?: string;
    accountNumber?: string;
    accountHolder?: string;
  };
  bankList?: { code: string; name: string }[];
  handleBankAccount: (verification: FormikProps<BankAccountVerifyFormikValues>) => Promise<void>;
  handleOpenExitConfirmationModal: () => void;
  closeModal: () => void;
  isUpdateBankAccount: boolean; // 계좌 번호 첫 인증인지 수정인지 판단하는 flag 변수
};

export default function BankAccountVerifyBody({
  form,
  bankList,
  handleBankAccount,
  handleOpenExitConfirmationModal,
  closeModal,
  isUpdateBankAccount,
}: BankAccountVerifyBodyProps) {
  const theme = useTheme();
  const formik = useFormik<BankAccountVerifyFormikValues>({
    enableReinitialize: true,
    initialValues: {
      agreeRevenueShareTerms: isUpdateBankAccount,
      acceptsPrivacyPolicy: isUpdateBankAccount,
      acceptsLegalResponsibility: isUpdateBankAccount,
      name: (isUpdateBankAccount ? form?.name : '') || '',
      userRegistrationNumber: (isUpdateBankAccount ? form?.userRegistrationNumber : '') || '',
      bankCode: (isUpdateBankAccount ? form?.bankCode : '') || '',
      accountNumber: (isUpdateBankAccount ? form?.accountNumber : '') || '',
      accountHolder: (isUpdateBankAccount ? form?.accountHolder : '') || '',
    },
    validationSchema: isUpdateBankAccount ? bankAccountModifySchema(form.name) : bankAccountVerifySchema(form.name),
    validateOnMount: true,
    onSubmit: () => {},
  });

  const handleBankAccountVerification = async () => {
    await handleBankAccount(formik);
  };

  const handleChangeCheckBox = (e: React.ChangeEvent) => {
    if (isUpdateBankAccount) return;
    formik.handleChange(e);
  };

  const buttonStyle = formik.isValid ? 'filled' : 'gray';

  const handleClickCloseButton = () => {
    // 값이 수정된 경우, 확인 모달을 띄워준다.
    if (formik.dirty) {
      handleOpenExitConfirmationModal();
    } else {
      closeModal();
    }
  };
  return (
    <>
      <SBankAccountVerificationHeader>
        <SBankAccountVerificationTitle>
          {isUpdateBankAccount ? t('chat.rewards_account_info_update') : t('chat.rewards_account_verification')}
        </SBankAccountVerificationTitle>
        <SCloseButton onClick={handleClickCloseButton}>
          <CloseIcon />
        </SCloseButton>
      </SBankAccountVerificationHeader>
      <SIdentityVerificationPage>
        <SWrapper>
          {/* <STitleContainer>
            <STitle>계좌 정보 입력</STitle>
            <ul className="description">
              <li>· 작품이 버블업에 선정된 작가님은 발생한 수익의 입금을 위한 계좌 정보가 필요해요.</li>
              <li>· 제출하신 정보는 암호화된 이후 적법한 절차에 따라 세무 신고에 사용 돼요.</li>
              <li>
                · 발생한 수익은 월 합산액이 1만원 이상일 경우, 수수료를 제외하고 익월 25일(휴일일 시 이전 영업일) 등록한
                계좌에 입금 돼요.
              </li>
              <li>
                · 등록한 계좌의 변경은 <span className="underline">help-bubble@bubbletap.com</span>으로 문의를 통해
                가능해요.
              </li>
            </ul>
          </STitleContainer> */}
          <SCheckContainer>
            <div className="check-box-container">
              <div className="check-box-row">
                <span className="text">
                  <a
                    href={`${BUBBLECHAT_WEB_PATH.WEB.POLICY.INDEX}#revenue`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'underline', textUnderlineOffset: '2px', color: '#138cf2' }}
                  >
                    {t('chat.rewards_profit_sharing_terms')}
                  </a>
                  에 동의합니다.
                </span>
                <label className={`check-box ${isUpdateBankAccount ? 'cursor-default' : ''}`}>
                  <input type="checkbox" name="agreeRevenueShareTerms" onChange={handleChangeCheckBox} />
                  {isUpdateBankAccount ? (
                    <CheckIcon variant={CheckIconVariant.AFFILIATE_DEFAULT} />
                  ) : (
                    <CheckIcon
                      variant={
                        formik.values.agreeRevenueShareTerms
                          ? CheckIconVariant.AFFILIATE_ON
                          : CheckIconVariant.AFFILIATE_OFF
                      }
                      color={formik.values.agreeRevenueShareTerms ? undefined : EColorMap.gray_1}
                    />
                  )}
                </label>
              </div>

              <div className="check-box-row">
                <span className="text">
                  {/* TODO PATH BUBBLECHAT_WEB_PATH.WEB.POLICY.BUBBLEUP_VERIFICATION*/}
                  <Trans
                    i18nKey={'chat.rewards_agree_personal_information'}
                    components={{
                      link1: (
                        <a
                          href={`${BUBBLECHAT_WEB_PATH.WEB.POLICY.INDEX}#privacy`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px',
                            color: theme.color.text.TXT_3,
                          }}
                        />
                      ),
                    }}
                  />
                  {/* 개인 정보 수집 및 이용 에 동의합니다. */}
                </span>
                <label className={`check-box ${isUpdateBankAccount ? 'cursor-default' : ''}`}>
                  <input type="checkbox" name="acceptsPrivacyPolicy" onChange={handleChangeCheckBox} />
                  {isUpdateBankAccount ? (
                    <CheckIcon variant={CheckIconVariant.AFFILIATE_DEFAULT} />
                  ) : (
                    <CheckIcon
                      variant={
                        formik.values.acceptsPrivacyPolicy
                          ? CheckIconVariant.AFFILIATE_ON
                          : CheckIconVariant.AFFILIATE_OFF
                      }
                      color={formik.values.acceptsPrivacyPolicy ? undefined : EColorMap.gray_1}
                    />
                  )}
                </label>
              </div>

              <div className="check-box-row">
                <span className="text">{t('chat.rewards_legal_responsibility')}</span>
                <label className={`check-box ${isUpdateBankAccount ? 'cursor-default' : ''}`}>
                  <input type="checkbox" name="acceptsLegalResponsibility" onChange={handleChangeCheckBox} />
                  {isUpdateBankAccount ? (
                    <CheckIcon variant={CheckIconVariant.AFFILIATE_DEFAULT} />
                  ) : (
                    <CheckIcon
                      variant={
                        formik.values.acceptsLegalResponsibility
                          ? CheckIconVariant.AFFILIATE_ON
                          : CheckIconVariant.AFFILIATE_OFF
                      }
                      color={formik.values.acceptsLegalResponsibility ? undefined : EColorMap.gray_1}
                    />
                  )}
                </label>
              </div>
            </div>
          </SCheckContainer>
          <SInputContainer>
            <div className="verification-info-container">
              <InputFormikWithLimit
                formik={formik}
                name="name"
                label="이름"
                placeholder="이름"
                style={{ width: '100%', boxSizing: 'border-box' }}
                inputStyle={{ fontSize: '16px' }}
                disabledStyle={{ WebkitTextFillColor: defaultTheme.color.gray2 }}
                validationSentence={formik.errors.name ?? t('chat.rewards_real name_id')}
                isResetBtn={false}
                autoComplete="off"
                disabled={isUpdateBankAccount}
              />
              <UserRegistrationNumberInputForm
                formik={formik}
                name="userRegistrationNumber"
                label="주민등록번호"
                placeholder="주민등록번호"
                style={{ width: '100%', boxSizing: 'border-box' }}
                inputStyle={{ fontSize: '16px' }}
                disabledStyle={{ WebkitTextFillColor: defaultTheme.color.gray2 }}
                validationSentence={formik.errors.userRegistrationNumber ?? `${t('chat.rewards_digit_number')}`}
                isResetBtn={false}
                limitCount={13}
                enterSubmit={formik.handleSubmit}
                autoComplete="off"
                disabled={isUpdateBankAccount}
              />
              <SelectFormik
                formik={formik}
                placeholder={'은행을 선택해 주세요.'}
                label={'은행명'}
                name={'bankCode'}
                options={bankList || []}
              />
              <InputFormikWithLimit
                formik={formik}
                name="accountNumber"
                label="계좌번호"
                placeholder="계좌번호"
                disabledStyle={{ WebkitTextFillColor: defaultTheme.color.gray2 }}
                enterSubmit={formik.handleSubmit}
                validationSentence={formik.errors.accountNumber ?? `${t('chat.rewards_only_number')}`}
                isResetBtn={true}
                limitCount={20}
                autoComplete="off"
              />
              <InputFormikWithLimit
                formik={formik}
                name="accountHolder"
                label="예금주명"
                placeholder={'예금주명'}
                disabledStyle={{ WebkitTextFillColor: defaultTheme.color.gray2 }}
                validationSentence={
                  formik.errors.accountHolder ?? `${t('chat.rewards_account_holder_name_description')}`
                }
                isResetBtn={false}
                autoComplete="off"
                disabled={isUpdateBankAccount}
              />
            </div>
            <RoundBtn
              design={buttonStyle}
              style={{
                marginTop: 24,
                height: '48px',
                borderRadius: '8px !important',
                fontSize: '14px !important',
                // cursor: isVerified ? 'default' : undefined,
                // '&:disabled': { border: 'none !important' },
              }}
              disabled={!formik.isValid}
              onClick={handleBankAccountVerification}
            >
              인증하기
            </RoundBtn>
          </SInputContainer>
        </SWrapper>
      </SIdentityVerificationPage>
    </>
  );
}

const SBankAccountVerificationHeader = styled.div`
  height: 60px;
  position: relative;
  display: flex;
  margin-bottom: 12px;
  justify-content: center;
  align-items: center;
`;
const SBankAccountVerificationTitle = styled.div`
  ${({ theme }) => theme.font.head5_sb};
  text-align: center;
`;
const SCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 1px;
  width: 20px;
`;

const SIdentityVerificationPage = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  .main-color {
    color: ${({ theme }) => theme.color.static.primary};
  }
`;

const PROFILE_LAYOUT_GAP = {
  LARGE: 40,
  MEDIUM: 30,
  SMALL: 24,
  XSMALL: 20,
};

export const SWrapper = styled.div`
  width: inherit;
  max-width: calc(1220px + 16px * 2);
  box-sizing: content-box;
  padding-right: 4px;
  height: calc(630px - 72px - 40px); // 72 = 모달 헤더, 40 = 모달 상하 패딩값
  overflow-y: scroll;
  overflow-x: hidden;

  /* Webkit 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 4px;
    height: 28px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background: #ccc;
  }
`;

export const SContainer = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: ${PROFILE_LAYOUT_GAP.LARGE}px;

  @media ${({ theme }) => theme.device.large} {
    margin-top: ${PROFILE_LAYOUT_GAP.MEDIUM}px;
  }
  @media ${({ theme }) => theme.device.medium} {
    margin-top: ${PROFILE_LAYOUT_GAP.XSMALL}px;
  }
`;

const SCheckContainer = styled.section`
  margin-top: 16px;

  .check-box-container {
    display: flex;
    flex-direction: column;
    row-gap: 16px;

    &.active {
      border: 1px solid ${({ theme }) => theme.color.static.primary};
    }
    &.error {
      border: 1px solid ${({ theme }) => theme.color.static.warning};
    }
  }

  .check-box-row {
    display: flex;
    justify-content: space-between;
    gap: 10px;

    .text {
      flex: 1;
      ${({ theme }) => theme.font.body2_sb};
    }

    .check-box {
      cursor: pointer;

      input {
        display: none;
      }

      &.cursor-default {
        cursor: initial;
      }
    }
  }
`;

const SInputContainer = styled.section`
  margin-top: 24px;
  width: 100%;
  display: flex;
  flex-direction: column;

  .verification-info-container {
    display: flex;
    flex-direction: column;
    gap: 32px;
    ${({ theme }) => theme.font.body2_m};
  }
`;
